import { useQueryClient } from '@tanstack/react-query';
import { useForm, useWatch } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { toast } from 'sonner';
import { OrgOnboardingProfileSchema } from '@reflex/shared';
import {
  getGetCompanyQueryKey,
  useCreateCompanyOrganization,
} from '@reflex/ui/client/generated/company';
import { ApiError } from '@reflex/ui/client/http';
import { Button } from '@reflex/ui/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@reflex/ui/components/ui/dialog';
import { Input } from '@reflex/ui/components/ui/input';
import { Label } from '@reflex/ui/components/ui/label';
import { OrgOnboardingProfileSelector } from '@/components/admin/OrgOnboardingProfileSelector';
import { FormField } from '@/components/forms/FormField';
import { useRefreshSession } from '@/hooks/auth/useSessionRefresh';

/**
 * The form is a deliberate subset of the self-serve create form: no company
 * fields, because the company already exists and is the one the caller came
 * in through, and no plan, because the new organization inherits the
 * company's. What is left is what genuinely differs between siblings — a
 * name, its URL, and the starting plugin set.
 */
const addOrganizationSchema = z.object({
  name: z.string().trim().min(1, 'Organization name is required').max(100),
  /**
   * Optional, and empty by default rather than auto-filled from the name.
   * The server derives a slug under the company's own slug when none is
   * sent, and this surface deliberately never sees that slug — so a
   * pre-filled field here could only show a value the server would then
   * take literally, quietly opting the organization out of the family
   * naming the company page is about.
   */
  slug: z
    .string()
    .trim()
    .regex(/^[a-z0-9-]+$/, 'Lowercase letters, numbers, and dashes only')
    .max(60)
    .optional()
    .or(z.literal('')),
  orgOnboardingProfile: z.enum(OrgOnboardingProfileSchema.options, {
    message: 'Choose the kind of work this organization will use Reflex for',
  }),
});

type AddOrganizationFormValues = z.infer<typeof addOrganizationSchema>;

export interface AddCompanyOrganizationDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** The company's name, so the dialog can say which company is growing. */
  companyName: string;
  /**
   * The active organization — the door the write is authorized through. It
   * keys the company query the new organization has to appear in, which is
   * per-organization for the reason `CompanyPage` documents.
   */
  organizationId: string;
}

/**
 * Configure → Company → Overview's one write: add a sibling organization to
 * the company that owns the active one.
 *
 * The new organization is a fresh, empty tenant — its own members, roles,
 * credentials and data — that happens to be owned by the same company, so it
 * inherits the plan and the shared identity connection without anyone
 * re-negotiating a contract. The caller becomes its owner AND its Company
 * Manager (the server carries the company authority they were admitted here
 * with into the organization they added, without which nobody could ever
 * configure its identity), which is why the session is refreshed on success:
 * their membership list just grew, and the org switcher is where they will
 * look for it.
 */
export function AddCompanyOrganizationDialog({
  open,
  onOpenChange,
  companyName,
  organizationId,
}: AddCompanyOrganizationDialogProps) {
  const queryClient = useQueryClient();
  const refreshSession = useRefreshSession();
  const {
    register,
    handleSubmit,
    reset,
    setValue,
    setError,
    control,
    formState: { errors },
  } = useForm<AddOrganizationFormValues>({
    resolver: zodResolver(addOrganizationSchema),
    defaultValues: { name: '', slug: '', orgOnboardingProfile: 'engineering' },
  });
  const selectedProfile = useWatch({ control, name: 'orgOnboardingProfile' });

  const mutation = useCreateCompanyOrganization({
    mutation: {
      onSuccess: async (organization) => {
        // The URL is named in the receipt because the admin may not have
        // chosen it: an omitted slug is derived by the server.
        toast.success(`${organization.name} added to ${companyName}`, {
          description: `Its URL is /orgs/${organization.slug}`,
        });
        // The roster on the page behind the dialog is the confirmation, so
        // refetch it before closing rather than optimistically splicing a row
        // the server may have slugged differently.
        await queryClient.invalidateQueries({
          queryKey: [...getGetCompanyQueryKey(), organizationId],
        });
        // The caller owns the organization they just added: their memberships
        // (and so the org switcher) are stale until the session reloads.
        refreshSession();
        handleOpenChange(false);
      },
      onError: (error: unknown) => {
        // A refused slug is the one failure the admin can act on in this
        // form, so it belongs on the field rather than in a toast that
        // disappears while they are still looking at the input. The server
        // refuses a reserved or empty-after-normalisation slug with a 400
        // whose message names the field ("Slug must contain…", "The slug
        // \"admin\" is reserved…").
        if (error instanceof ApiError && error.status === 400 && /slug/i.test(error.message)) {
          setError('slug', { type: 'server', message: error.message });
          return;
        }
        toast.error('Failed to add the organization', {
          description: error instanceof Error ? error.message : undefined,
        });
      },
    },
  });

  function handleOpenChange(next: boolean) {
    onOpenChange(next);
    if (!next) reset();
  }

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent data-testid="add-company-organization-dialog">
        <DialogHeader>
          <DialogTitle>Add an organization</DialogTitle>
          <DialogDescription>
            A new organization under {companyName}. It joins the company&rsquo;s plan and identity
            provider, and starts empty: its own members, roles, credentials, and data. Signing in
            through the company&rsquo;s provider stays off until you turn it on in the new
            organization&rsquo;s security settings.
          </DialogDescription>
        </DialogHeader>
        <form
          onSubmit={handleSubmit((values) =>
            mutation.mutate({
              data: {
                name: values.name,
                // Omitted, not empty: the server derives the family slug only
                // when the field is absent.
                slug: values.slug || undefined,
                orgOnboardingProfile: values.orgOnboardingProfile,
              },
            }),
          )}
          className="space-y-4"
        >
          <div className="space-y-1">
            <Label htmlFor="company-org-name">Organization name</Label>
            <Input
              id="company-org-name"
              placeholder="Finance"
              autoComplete="off"
              {...register('name')}
            />
            {errors.name && <p className="text-xs text-destructive">{errors.name.message}</p>}
          </div>
          <div className="space-y-1">
            <Label htmlFor="company-org-slug">URL slug (optional)</Label>
            <Input
              id="company-org-slug"
              placeholder="finance"
              autoComplete="off"
              {...register('slug', {
                setValueAs: (v: unknown) => String(v ?? '').toLowerCase(),
              })}
            />
            <p className="text-xs text-muted-foreground">
              Leave this blank to put the new organization under your company&rsquo;s slug, next to
              its siblings.
            </p>
            {errors.slug && <p className="text-xs text-destructive">{errors.slug.message}</p>}
          </div>
          <FormField
            label="What kind of work will this organization use Reflex for?"
            labelId="company-org-kind-label"
            required
            hint="Reflex uses this to set up relevant plugins. The new organization's admins can change them any time."
            error={errors.orgOnboardingProfile?.message}
          >
            <OrgOnboardingProfileSelector
              value={selectedProfile}
              onChange={(profile) =>
                setValue('orgOnboardingProfile', profile, {
                  shouldValidate: true,
                  shouldDirty: true,
                })
              }
              labelId="company-org-kind-label"
              required
            />
          </FormField>
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => handleOpenChange(false)}
              disabled={mutation.isPending}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={mutation.isPending}>
              {mutation.isPending ? 'Adding…' : 'Add organization'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
